import { useMutation } from '@apollo/client';
import type { UseCreateLookalikeSegment } from '@openx/components-segment/lib/utils/context/SegmentHookTypes';
import type { CreateLookalikeSegmentInput } from '@openx/types/segments';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CreateLookalikeObjectsDocument } from '../../../graphql/mutation/segments/CreateLookalikeObjects.generated';
import { NestedErrorCode } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';

export const useCreateLookalikeSegment: UseCreateLookalikeSegment = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [createLookalikeObjects, { loading }] = useMutation(CreateLookalikeObjectsDocument);

  const createLookalikeSegment = useCallback(
    async (input: CreateLookalikeSegmentInput) => {
      const createLookalikeResponse = await createLookalikeObjects({
        refetchQueries: ['GetLalSegmentsList', 'GetLalSegmentFeed'],
        variables: {
          definition: input.definition,
          seedSegmentIds: input.seedSegmentIds,
        },
        update: (cache, mutationResult) => {
          if (!mutationResult.data?.createLookalikeObjects) {
            return;
          }

          cache.evict({ fieldName: 'lookalike_definition', id: 'ROOT_QUERY' });
          cache.gc();
        },
      });

      if (createLookalikeResponse.data?.createLookalikeObjects) {
        enqueueSnackbar(
          t('Lookalike segment {segmentName} was successfully created', {
            segmentName: input.definition.lookalike_name,
          }),
          { variant: 'success' }
        );
        navigate('/segments/lookalike');
      }
    },
    [createLookalikeObjects, navigate, t]
  );

  const errorCallback = useCallback(
    error => {
      if (error.code !== NestedErrorCode.JOINT_UNIQUENESS_VIOLATION || !error.fields.includes('lookalike_name'))
        return false;

      if (error.path.indexOf('account_id') !== -1) return true;

      enqueueSnackbar(t('Lookalike segment with such name already exists'), { variant: 'error' });
      return true;
    },
    [t]
  );
  useLocalErrorHandling(CreateLookalikeObjectsDocument, errorCallback);

  return {
    createLookalikeSegment,
    isCreating: loading,
  };
};
